import { useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./PromotionPage.css";

// 학원 관리 > 프로모션(2026-09-30). '담당 학교와 교과서' 한 줄이 무료 자료 링크 하나다.
// 줄마다 블로그·카페·인스타·쓰레드·당근 글(10가지 중 하나)과 세로·정사각 이미지를 만들어 둔다.
// 글마다 주소 끝(?c=blog 등)이 달라 신청이 어디서 왔는지 승인 목록과 텔레그램 알림에 뜬다.
// 링크로 들어온 분이 닉네임을 적어 신청하면 아래 승인 목록에서 승인한다(학교·교과서가 함께 보인다).
// 자료가 아직 없는 교과서·부교재도 링크는 미리 만든다. 받기는 자료가 올라간 뒤 열린다.
// 서버: api/echo/academy_promo_views.py, 글: api/echo/academy_promo_texts.py

const STATUS_LABEL = { pending: "승인 대기", approved: "승인", rejected: "거절" };
const CUSTOM_TEXTBOOK = "__custom__";
const PLATFORM_LIST = [
  { key: "blog_list", label: "블로그" },
  { key: "cafe_list", label: "카페" },
  { key: "insta_list", label: "인스타" },
  { key: "threads_list", label: "쓰레드" },
  { key: "daangn_list", label: "당근" },
];

const formatTime = (value) =>
  new Date(value).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    window.prompt("아래 글을 복사해 주세요", text);
  }
};

const toEditRow = (row) => ({
  key: row.key,
  school_name: row.school_name,
  grade: row.grade,
  textbook_code: row.textbook_code || (row.custom_textbook ? CUSTOM_TEXTBOOK : ""),
  custom_textbook: row.custom_textbook || "",
});

const GrammarPromotion = () => {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("");
  const [editRowList, setEditRowList] = useState([]);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState("");
  const [openKey, setOpenKey] = useState("");
  const [variantOffsetMap, setVariantOffsetMap] = useState({});
  const [missingOpen, setMissingOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const apply = (next) => {
    setData(next);
    setRegion(next.region || "");
    setEditRowList((next.row_list || []).map(toEditRow));
  };

  const load = () => apiClient.get("/api/v1/echo/teacher/promo", { withAuth: true }).then((response) => apply(response.data));

  useEffect(() => {
    load();
  }, []);

  if (!data) return null;

  const savedRowMap = Object.fromEntries((data.row_list || []).map((row) => [row.key, row]));
  const textbookList = data.textbook_list || [];

  const updateRow = (index, patch) =>
    setEditRowList((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  const addRow = () => setEditRowList((prev) => [...prev, { school_name: "", grade: "", textbook_code: "", custom_textbook: "" }]);
  const removeRow = (index) => setEditRowList((prev) => prev.filter((_, rowIndex) => rowIndex !== index));

  const save = async () => {
    setMessage("");
    const incomplete = editRowList.some(
      (row) =>
        !row.school_name.trim() ||
        !row.grade ||
        !row.textbook_code ||
        (row.textbook_code === CUSTOM_TEXTBOOK && !row.custom_textbook.trim())
    );
    if (incomplete) {
      setMessage("학교 이름, 학년, 교과서(목록에 없으면 직접 적기)를 모두 채워 주세요. 빈 줄은 삭제해 주세요.");
      return;
    }
    setIsSaving(true);
    try {
      const response = await apiClient.put(
        "/api/v1/echo/teacher/promo",
        {
          region,
          row_list: editRowList.map((row) => ({
            key: row.key,
            school_name: row.school_name,
            grade: row.grade,
            textbook_code: row.textbook_code === CUSTOM_TEXTBOOK ? "" : row.textbook_code,
            custom_textbook: row.textbook_code === CUSTOM_TEXTBOOK ? row.custom_textbook : "",
          })),
        },
        { withAuth: true }
      );
      apply(response.data);
      setMessage("저장했어요. 줄마다 링크와 이미지 · 글이 만들어졌어요.");
      if ((response.data.row_list || []).some((row) => !row.file_ready)) setMissingOpen(true);
    } catch (caught) {
      setMessage(caught.response?.data?.error || "저장하지 못했어요.");
    } finally {
      setIsSaving(false);
    }
  };

  const copy = async (id, text) => {
    await copyText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const variantOf = (row) => ((row.first_index || 0) + (variantOffsetMap[row.key] || 0)) % ((row.blog_list || []).length || 1);
  const nextVariant = (key) => setVariantOffsetMap((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }));

  const decide = async (requestId, action) => {
    await apiClient.post("/api/v1/echo/teacher/promo/decide", { request_id: requestId, action }, { withAuth: true });
    load();
  };

  const requestList = data.request_list || [];
  const pendingList = requestList.filter((one) => one.status === "pending");
  const shownList = showAll ? requestList : pendingList;

  return (
    <div className="promo-page">
      <div className="section-title">프로모션</div>
      <p className="promo-page__lead">
        담당 학교와 교과서를 적으면 줄마다 <b>무료 자료 링크</b>와 <b>블로그 · 인스타 · 쓰레드 · 당근 글</b>, <b>이미지</b>가 만들어져요.
        글에는 지역 · 학교 · 학원 · 출판사가 들어가 검색에 잘 걸려요. 올린 뒤 들어온 신청은 아래에서 <b>승인</b>만 누르면 돼요.
      </p>

      <section className="promo-page__card">
        <div className="promo-page__card-title">담당 학교와 교과서</div>
        <label className="promo-page__region">
          <span>지역</span>
          <input value={region} placeholder="예: 대전 유성구" onChange={(event) => setRegion(event.target.value)} />
        </label>

        {editRowList.map((row, index) => {
          const saved = row.key ? savedRowMap[row.key] : null;
          const variant = saved ? variantOf(saved) : 0;
          return (
            <div key={row.key || `new-${index}`} className="promo-page__row-block">
              <div className="promo-page__school-row">
                <input
                  value={row.school_name}
                  placeholder="학교 이름 (예: 어은중)"
                  onChange={(event) => updateRow(index, { school_name: event.target.value })}
                />
                <select
                  value={row.grade}
                  onChange={(event) =>
                    updateRow(index, {
                      grade: event.target.value,
                      textbook_code: row.textbook_code === CUSTOM_TEXTBOOK ? CUSTOM_TEXTBOOK : "",
                    })
                  }
                >
                  <option value="">학년</option>
                  {(data.grade_list || []).map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
                <select
                  value={row.textbook_code}
                  disabled={!row.grade}
                  onChange={(event) => updateRow(index, { textbook_code: event.target.value })}
                >
                  <option value="">{row.grade ? "교과서 선택" : "학년을 먼저 고르세요"}</option>
                  {textbookList
                    .filter((book) => book.grade === row.grade)
                    .map((book) => (
                      <option key={book.code} value={book.code}>
                        {book.label} ({book.subject})
                      </option>
                    ))}
                  <option value={CUSTOM_TEXTBOOK}>목록에 없어요 (직접 적기 · 부교재)</option>
                </select>
                <button type="button" className="promo-page__row-remove" onClick={() => removeRow(index)}>
                  삭제
                </button>
              </div>
              {row.textbook_code === CUSTOM_TEXTBOOK && (
                <input
                  className="promo-page__custom"
                  value={row.custom_textbook}
                  placeholder="교과서나 부교재 이름 (예: 능률 양현권, 리딩튜터 주니어 2)"
                  onChange={(event) => updateRow(index, { custom_textbook: event.target.value })}
                />
              )}

              {!saved && <p className="promo-page__help">저장하면 이 줄의 링크와 이미지 · 글이 만들어져요.</p>}
              {saved && (
                <div className="promo-page__share">
                  <div className="promo-page__share-head">
                    <b>{saved.title}</b>
                    {!saved.file_ready && (
                      <button type="button" className="promo-page__badge promo-page__badge--wait" onClick={() => setMissingOpen(true)}>
                        자료 준비 중
                      </button>
                    )}
                    {saved.pending_count > 0 && <em className="promo-page__badge promo-page__badge--wait">대기 {saved.pending_count}</em>}
                    {saved.approved_count > 0 && <em className="promo-page__badge promo-page__badge--ok">승인 {saved.approved_count}</em>}
                  </div>
                  {!saved.file_ready && <p className="promo-page__missing">{data.material_missing_message}</p>}
                  <div className="promo-page__actions">
                    {PLATFORM_LIST.map((platform) => (
                      <button
                        key={platform.key}
                        type="button"
                        onClick={() => copy(`${saved.key}-${platform.key}`, (saved[platform.key] || [])[variant] || "")}
                      >
                        {copied === `${saved.key}-${platform.key}` ? "복사했어요" : `${platform.label} 글 복사`}
                      </button>
                    ))}
                    <a className="promo-page__image-link" href={saved.card_portrait_url} download target="_blank" rel="noreferrer">
                      세로 이미지
                    </a>
                    <a className="promo-page__image-link" href={saved.card_square_url} download target="_blank" rel="noreferrer">
                      정사각 이미지
                    </a>
                    <button type="button" onClick={() => copy(`${saved.key}-url`, saved.url)}>
                      {copied === `${saved.key}-url` ? "복사했어요" : "링크 복사"}
                    </button>
                    <button type="button" className="promo-page__ghost" onClick={() => nextVariant(saved.key)}>
                      다른 글로 ({variant + 1}/{(saved.blog_list || []).length})
                    </button>
                    <button type="button" className="promo-page__ghost" onClick={() => setOpenKey(openKey === saved.key ? "" : saved.key)}>
                      {openKey === saved.key ? "미리보기 접기" : "미리보기"}
                    </button>
                  </div>
                  {openKey === saved.key && (
                    <div className="promo-page__preview">
                      {PLATFORM_LIST.map((platform) => (
                        <div key={platform.key}>
                          <b>{platform.label}</b>
                          <pre>{(saved[platform.key] || [])[variant]}</pre>
                        </div>
                      ))}
                      <div className="promo-page__cards">
                        <b>함께 올릴 이미지</b>
                        <div>
                          <img src={saved.card_portrait_url} alt="세로 이미지" loading="lazy" />
                          <img src={saved.card_square_url} alt="정사각 이미지" loading="lazy" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div className="promo-page__row-foot">
          <button type="button" className="promo-page__row-add" onClick={addRow}>+ 학교 추가</button>
          <button type="button" className="promo-page__primary" disabled={isSaving} onClick={save}>
            {isSaving ? "저장 중..." : "저장하기"}
          </button>
        </div>
        {message && <p className="promo-page__message">{message}</p>}
      </section>

      {missingOpen && (
        <div className="promo-page__popup" role="dialog" aria-modal="true" onClick={() => setMissingOpen(false)}>
          <div className="promo-page__popup-card" onClick={(event) => event.stopPropagation()}>
            <b>아직 지문 자료가 없어요</b>
            <p>
              링크와 이미지 · 글은 미리 만들어 두었어요.
              <br />
              다운로드는 자료가 준비된 뒤에 열려요.
              <br />
              <strong>지문을 유현쌤께 꼭 전달해 주세요!</strong>
            </p>
            <button type="button" className="promo-page__primary" onClick={() => setMissingOpen(false)}>확인</button>
          </div>
        </div>
      )}

      <section className="promo-page__card">
        <div className="promo-page__card-head">
          <div className="promo-page__card-title">승인 목록 {pendingList.length > 0 ? `· 승인 대기 ${pendingList.length}명` : ""}</div>
          <button type="button" className="promo-page__toggle" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? "승인 대기만 보기" : `전체 보기 (${requestList.length})`}
          </button>
        </div>
        <p className="promo-page__help">
          승인하면 그분이 신청한 기기(같은 브라우저나 같은 인터넷)에서 바로 받을 수 있어요. 승인한 뒤 <b>답글 복사</b>로 댓글에 답을 달아 주세요.
        </p>
        {shownList.length === 0 ? (
          <p className="promo-page__help">{showAll ? "아직 신청이 없어요." : "승인을 기다리는 신청이 없어요."}</p>
        ) : (
          shownList.map((one) => (
            <div key={one.id} className="promo-page__row">
              <div className="promo-page__who">
                <b>{one.title}</b>
                <span>
                  {one.nickname}
                  {one.source_label ? ` · ${one.source_label}에서` : ""} · {formatTime(one.created_at)}
                  {one.download_count > 0 ? ` · ${one.download_count}번 받음` : ""}
                </span>
              </div>
              <span className={`promo-page__status promo-page__status--${one.status}`}>{STATUS_LABEL[one.status]}</span>
              <div className="promo-page__row-actions">
                {one.status !== "approved" && (
                  <button type="button" className="promo-page__approve" onClick={() => decide(one.id, "approve")}>승인</button>
                )}
                {one.status === "approved" && (
                  <button type="button" className="promo-page__reject" onClick={() => copy(`reply-${one.id}`, one.reply_text)}>
                    {copied === `reply-${one.id}` ? "복사했어요" : "답글 복사"}
                  </button>
                )}
                {one.status !== "rejected" && (
                  <button type="button" className="promo-page__reject" onClick={() => decide(one.id, "reject")}>거절</button>
                )}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
};

// 프로모션 종류(2026-10-04). 문법자료(위의 것) / 파닉스(학원 소개 + 바로 응시, api/echo/promo_test.py) / 레벨테스트(나중)
const KIND_LIST = [
  { key: "grammar", label: "문법자료" },
  { key: "phonics", label: "파닉스" },
  { key: "leveltest", label: "레벨테스트" },
];
const ENTRY_STATUS_LABEL = { issued: "코드 받음", started: "응시 중", completed: "응시 완료" };

const FEE_TYPE_LIST = [
  { key: "free", label: "무료" },
  { key: "discount", label: "할인" },
  { key: "paid", label: "유료" },
];
// 학원별 정보 칸. 글자 수는 서버 칸 길이와 같게 둔다(페이지·카드 칸이 무너지지 않게).
const PROFILE_INPUT_LIST = [
  { key: "director_name", label: "원장 이름", max: 20 },
  { key: "period_label", label: "기간", max: 20, placeholder: "3개월" },
  { key: "season_label", label: "해당 달", max: 30, placeholder: "10·11·12월" },
  { key: "free_condition", label: "무료·할인 조건", max: 40, placeholder: "레벨테스트 50점 이하" },
  { key: "lesson_text", label: "수업 횟수", max: 40, placeholder: "주 3회 · 회당 50분" },
  { key: "registered_name", label: "등록상 학원명", max: 60 },
  { key: "registration_number", label: "학원 등록번호", max: 40, placeholder: "제 0000호" },
  { key: "tuition_text", label: "정규 수강료(월)", max: 150, placeholder: "초등 영어 15만원 · 중등 영어 25만원" },
  { key: "qr_url", label: "카드 QR 주소", max: 300, placeholder: "학원 블로그·인스타 주소(비우면 레벨테스트 페이지)" },
];

const PhonicsProfileForm = ({ data, onSaved }) => {
  const [form, setForm] = useState(data.profile);
  const [message, setMessage] = useState("");
  useEffect(() => setForm(data.profile), [data.profile]);

  const update = (key) => (event) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const save = () =>
    apiClient
      .put("/api/v1/echo/teacher/promo/phonics", form, { withAuth: true })
      .then((response) => {
        onSaved(response.data);
        setMessage("저장했어요.");
      })
      .catch(() => setMessage("저장하지 못했어요."));

  return (
    <section className="promo-page__card">
      <div className="promo-page__card-title">학원 정보</div>
      <div className="promo-phonics__partner">
        {data.partner.photo_url && <img src={data.partner.photo_url} alt="원장 사진" />}
        {data.partner.space_photo_url && (
          <img className="promo-phonics__space" src={data.partner.space_photo_url} alt="학원 사진" />
        )}
        <div>
          <b>{data.partner.name}</b>
          <span>{data.partner.address}</span>
          <span>{data.partner.phone}</span>
          <a href="/academy/partner-info">협력학원 관리에서 고치기</a>
        </div>
      </div>
      <div className="promo-phonics__form">
        <label className="promo-phonics__wide">
          원장 약력(한 줄에 하나, 네 줄까지)
          <textarea rows={4} maxLength={200} value={form.director_career || ""} onChange={update("director_career")} />
        </label>
        <label>
          파닉스반 수업료
          <div className="promo-phonics__fee">
            {FEE_TYPE_LIST.map((one) => (
              <button
                type="button"
                key={one.key}
                aria-pressed={form.fee_type === one.key}
                onClick={() => setForm((previous) => ({ ...previous, fee_type: one.key }))}
              >
                {one.label}
              </button>
            ))}
          </div>
        </label>
        {form.fee_type !== "free" && (
          <label>
            수업료(월, 원)
            <input inputMode="numeric" value={form.fee_amount ?? ""} onChange={update("fee_amount")} />
          </label>
        )}
        {PROFILE_INPUT_LIST.map((one) => (
          <label key={one.key} className={one.max > 60 ? "promo-phonics__wide" : ""}>
            {one.label}
            <input maxLength={one.max} placeholder={one.placeholder || ""} value={form[one.key] || ""} onChange={update(one.key)} />
          </label>
        ))}
      </div>
      <div className="promo-phonics__save">
        {data.missing_list.length > 0 && <span className="promo-phonics__missing">필수: {data.missing_list.join(", ")}</span>}
        {message && <span className="promo-page__help">{message}</span>}
        <button type="button" className="promo-phonics__make" onClick={save}>
          저장
        </button>
      </div>
    </section>
  );
};

// 채널별 공유 글. 블로그는 셋(셋째는 카드 6장 글), 나머지는 둘이라 넘길 때 채널마다 돌려 쓴다.
// 글은 서버(api/echo/phonics_promo_texts.py)가 학원 이름·구·동·수업료 조건을 넣어 만든다.
const PhonicsShareTexts = ({ textMap }) => {
  const [variant, setVariant] = useState(0);
  const [copied, setCopied] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const variantCount = (textMap.blog_list || []).length || 1;
  const textOf = (key) => {
    const list = textMap[key] || [];
    return list.length ? list[variant % list.length] : "";
  };

  const copy = async (key) => {
    await copyText(textOf(key));
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <>
      <div className="promo-page__actions">
        {PLATFORM_LIST.map((platform) => (
          <button key={platform.key} type="button" onClick={() => copy(platform.key)}>
            {copied === platform.key ? "복사했어요" : `${platform.label} 글 복사`}
          </button>
        ))}
        <button type="button" className="promo-page__ghost" onClick={() => setVariant((variant + 1) % variantCount)}>
          다른 글로 ({variant + 1}/{variantCount})
        </button>
        <button type="button" className="promo-page__ghost" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? "미리보기 접기" : "미리보기"}
        </button>
      </div>
      {isOpen && (
        <div className="promo-page__preview">
          {PLATFORM_LIST.map((platform) => (
            <div key={platform.key}>
              <b>{platform.label}</b>
              <pre>{textOf(platform.key)}</pre>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const PhonicsPromotion = () => {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    apiClient
      .get("/api/v1/echo/teacher/promo/phonics", { withAuth: true })
      .then((response) => setData(response.data))
      .catch(() => setMessage("불러오지 못했어요."));
  }, []);

  const createLink = () =>
    apiClient
      .post("/api/v1/echo/teacher/promo/phonics", {}, { withAuth: true })
      .then((response) => setData(response.data))
      .catch((error) => setMessage(error?.response?.data?.error || "링크를 만들지 못했어요."));

  if (!data) return message ? <p className="promo-page__help">{message}</p> : null;
  if (!data.is_available) return <p className="promo-page__help">Plus · Premium 학원에서 쓸 수 있어요.</p>;

  return (
    <>
      <PhonicsProfileForm data={data} onSaved={setData} />
      <section className="promo-page__card">
        <div className="promo-page__card-title">
          파닉스 프로모션 링크
          <span className={data.remaining_code_count > 0 ? "promo-phonics__codes" : "promo-phonics__codes promo-phonics__codes--empty"}>
            남은 응시코드 {data.remaining_code_count}장
          </span>
        </div>
        {data.url ? (
          <>
            <div className="promo-phonics__link">
              <a href={data.url} target="_blank" rel="noreferrer">
                {data.url}
              </a>
              <button type="button" onClick={() => copyText(data.url)}>
                복사
              </button>
            </div>
            <PhonicsShareTexts textMap={data.share_text_map || {}} />
            <div className="promo-phonics__channels">
              {data.channel_url_list.map((channel) => (
                <button type="button" key={channel.source} onClick={() => copyText(channel.url)}>
                  {channel.label} 링크 복사
                </button>
              ))}
            </div>
            <div className="promo-phonics__channels">
              {data.card_url_list.map((card) => (
                <a key={`${card.page}-${card.size}`} className="promo-phonics__card-link" href={card.url} target="_blank" rel="noreferrer">
                  {card.label}
                </a>
              ))}
            </div>
          </>
        ) : (
          <>
            <button type="button" className="promo-phonics__make" onClick={createLink} disabled={data.missing_list.length > 0}>
              링크 만들기
            </button>
            {message && <p className="promo-page__help">{message}</p>}
          </>
        )}
      </section>
      <section className="promo-page__card">
        <div className="promo-page__card-title">바로 응시 신청 {data.entry_list.length}명</div>
        {data.entry_list.length === 0 ? (
          <p className="promo-page__help">아직 신청이 없어요.</p>
        ) : (
          <table className="promo-phonics__table">
            <thead>
              <tr>
                <th>신청</th>
                <th>아이</th>
                <th>학부모 번호</th>
                <th>상태</th>
                <th>점수</th>
                <th>리포트</th>
              </tr>
            </thead>
            <tbody>
              {data.entry_list.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatTime(entry.created_at)}</td>
                  <td>
                    {entry.child_name} <span>{entry.grade}</span>
                  </td>
                  <td>
                    <a href={`tel:${entry.parent_phone}`}>{entry.parent_phone}</a>
                  </td>
                  <td>{ENTRY_STATUS_LABEL[entry.status]}</td>
                  <td>{entry.score === null ? "" : `${entry.score}점`}</td>
                  <td>
                    {entry.report_url && (
                      <a href={entry.report_url} target="_blank" rel="noreferrer">
                        리포트
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
};

const PromotionPage = () => {
  const [kind, setKind] = useState("grammar");
  return (
    <div>
      <div className="promo-kind-tabs">
        {KIND_LIST.map((one) => (
          <button
            type="button"
            key={one.key}
            aria-pressed={kind === one.key}
            onClick={() => setKind(one.key)}
          >
            {one.label}
          </button>
        ))}
      </div>
      {kind === "grammar" && <GrammarPromotion />}
      {kind === "phonics" && (
        <div className="promo-page">
          <PhonicsPromotion />
        </div>
      )}
      {kind === "leveltest" && (
        <div className="promo-page">
          <p className="promo-page__help">준비 중이에요.</p>
        </div>
      )}
    </div>
  );
};

export default PromotionPage;
